import React, { useState, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Onboarding.css';

import cap from '../assets/onboarding/cap.svg';
import capLight from '../assets/onboarding/cap-light.svg';
import goalImg from '../assets/onboarding/image.png';

import saudiFlag from '../assets/langs/image copy 3.png';
import frenchFlag from '../assets/langs/image copy 2.png';
import italianFlag from '../assets/langs/image copy.png';
import ukFlag from '../assets/langs/image.png';
import {
  computeDiagnosticResult,
  getAdjustedLevel,
  getLevelFromEducation,
  getPhaseQuestions,
} from '../utils/adaptiveDiagnostic';

const Onboarding = () => {
  const { isDarkTheme } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [language, setLanguage] = useState('');
  const [country, setCountry] = useState('');
  const [education, setEducation] = useState('');
  const [goal, setGoal] = useState('');
  const [diagnosticSession, setDiagnosticSession] = useState(null);
  const [diagnosticResult, setDiagnosticResult] = useState(null);

  const languages = useMemo(
    () => [
      { id: 'english', code: 'en', name: 'English', flag: ukFlag },
      { id: 'arabic', code: 'ar', name: 'Arabic', flag: saudiFlag },
      { id: 'french', code: 'fr', name: 'Français', flag: frenchFlag },
      { id: 'italian', code: 'it', name: 'Italiano', flag: italianFlag },
    ],
    []
  );
  const countries = useMemo(
    () => [
      { id: 'us', name: 'United States' },
      { id: 'france', name: 'France' },
      { id: 'italy', name: 'Italy' },
      { id: 'saudi', name: 'Saudi Arabia' },
    ],
    []
  );

  const frenchLevels = {
    primaire: [
      'CP (6-7)',
      'CE1 (7-8)',
      'CE2 (8-9)',
      'CM1 (9-10)',
      'CM2 (10-11)'
    ],
    college: [
      '6ème (11-12)',
      '5ème (12-13)',
      '4ème (13-14)',
      '3ème (14-15)'
    ],
    lycee: [
      '2nde (15-16)',
      '1ère (16-17)',
      'Terminale (17-18)'
    ]
  };

  const italianLevels = {
    primaria: [
      'Scuola primaria - Classe 1 (6-7)',
      'Scuola primaria - Classe 2 (7-8)',
      'Scuola primaria - Classe 3 (8-9)',
      'Scuola primaria - Classe 4 (9-10)',
      'Scuola primaria - Classe 5 (10-11)'
    ],
    medie: [
      'Prima media (11-12)',
      'Seconda media (12-13)',
      'Terza media (13-14)'
    ],
    liceo: [
      'Biennio 1 (14-16)',
      'Biennio 2 (16-17)',
      'Quinto anno (17-19)'
    ]
  };

  const genericLevels = {
    elementary: ['Elementary School (6-10)'],
    middle: ['Middle School (11-14)'],
    high: ['High School (14-18)']
  };

  const transitionDelay = 650;

  const advanceToStep = (nextStep) => {
    if (isTransitioning || nextStep === currentStep) {
      return;
    }
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(nextStep);
      setIsTransitioning(false);
    }, transitionDelay);
  };
  window.onload = function() {
    window.scrollTo(0, 0);
};


  const handleLanguageSelect = (languageId, code) => {
    if (isTransitioning) {
      return;
    }

    console.log('Selected language:', languageId, code);
    const resolvedCode = code || 'en';
    setLanguage(resolvedCode);

    try {
      setAppLanguage(resolvedCode);
    } catch (error) {
      console.warn('Failed to update app language', error);
    }

    setEducation('');
    setGoal('');
    setCountry('');
    setDiagnosticSession(null);
    setDiagnosticResult(null);
    advanceToStep(1);
  };

  const handleCountrySelect = (countryCode) => {
    if (isTransitioning) {
      return;
    }

    setCountry(countryCode);
    setEducation('');
    setGoal('');
    setDiagnosticSession(null);
    setDiagnosticResult(null);
    advanceToStep(2);
  };

  const handleEducationSelect = (eduLevel) => {
    if (isTransitioning) {
      return;
    }

    console.log('Selected education level:', eduLevel);
    setEducation(eduLevel);
    const baseLevel = getLevelFromEducation(eduLevel);
    const phase1Questions = getPhaseQuestions({ country, level: baseLevel, phase: 'phase1' });
    setDiagnosticSession({
      baseLevel,
      phase: 'phase1',
      currentIndex: 0,
      phase1Correct: 0,
      phase2Correct: 0,
      phase1Questions,
      phase2Questions: [],
    });
    setDiagnosticResult(null);
    advanceToStep(3);
  };

  const handleDiagnosticAnswer = (choiceIndex) => {
    if (!diagnosticSession) {
      return;
    }

    if (diagnosticSession.phase === 'phase1') {
      const currentQuestion = diagnosticSession.phase1Questions[diagnosticSession.currentIndex];
      const phase1Correct = diagnosticSession.phase1Correct + (currentQuestion?.correctIndex === choiceIndex ? 1 : 0);
      const nextIndex = diagnosticSession.currentIndex + 1;

      if (nextIndex < diagnosticSession.phase1Questions.length) {
        setDiagnosticSession((prev) => ({
          ...prev,
          phase1Correct,
          currentIndex: nextIndex,
        }));
        return;
      }

      const phase1Percent = diagnosticSession.phase1Questions.length
        ? (phase1Correct / diagnosticSession.phase1Questions.length) * 100
        : 0;
      const adjustedLevel = getAdjustedLevel(phase1Percent, diagnosticSession.baseLevel);
      const phase2Questions = getPhaseQuestions({ country, level: adjustedLevel, phase: 'phase2' });

      setDiagnosticSession((prev) => ({
        ...prev,
        phase: 'phase2',
        currentIndex: 0,
        phase1Correct,
        adjustedLevel,
        phase2Questions,
      }));
      return;
    }

    const currentQuestion = diagnosticSession.phase2Questions[diagnosticSession.currentIndex];
    const phase2Correct = diagnosticSession.phase2Correct + (currentQuestion?.correctIndex === choiceIndex ? 1 : 0);
    const nextIndex = diagnosticSession.currentIndex + 1;

    if (nextIndex < diagnosticSession.phase2Questions.length) {
      setDiagnosticSession((prev) => ({
        ...prev,
        phase2Correct,
        currentIndex: nextIndex,
      }));
      return;
    }

    const result = computeDiagnosticResult({
      country,
      baseLevel: diagnosticSession.baseLevel,
      phase1Correct: diagnosticSession.phase1Correct,
      phase2Correct,
      phase1Total: diagnosticSession.phase1Questions.length,
      phase2Total: diagnosticSession.phase2Questions.length,
    });
    setDiagnosticSession((prev) => ({
      ...prev,
      phase2Correct,
      phase: 'done',
      currentIndex: nextIndex,
    }));
    setDiagnosticResult(result);
  };

  const handleGoalSelect = async (selectedGoal) => {
    if (isTransitioning) {
      return;
    }

    setGoal(selectedGoal);
    setIsTransitioning(true);

    const data = {
      language,
      country,
      education,
      goal: selectedGoal,
      diagnosticResult,
      initialLearningLevel: diagnosticResult?.classification || 'At expected level',
      estimatedSchoolLevel: diagnosticResult?.estimatedSchoolLevel || education,
      confidenceScore: diagnosticResult?.confidenceScore || 50,
      recommendedStartingUnits: diagnosticResult?.recommendedUnits || [],
      completedAt: new Date().toISOString(),
    };

    if (user) {
      try {
        await updateProfile({
          onboarding_data: data,
          language_preference: language,
          education_level: diagnosticResult?.estimatedSchoolLevel || education,
          learning_goal: selectedGoal,
        });
        console.log('Saved onboarding to Supabase (goal selection)');
      } catch (err) {
        console.warn('Failed to save onboarding to Supabase (goal selection)', err);
      }
    }

    setTimeout(() => {
      navigate('/signup');
    }, transitionDelay);
  };

  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const { language: appLang, setLanguage: setAppLanguage, t } = useLanguage();

  const getEduGroups = () => {
    if (appLang === 'fr') {
      return [
        { title: 'École primaire', levels: frenchLevels.primaire },
        { title: 'Collège', levels: frenchLevels.college },
        { title: 'Lycée', levels: frenchLevels.lycee }
      ];
    }

    if (appLang === 'it') {
      return [
        { title: 'Scuola primaria', levels: italianLevels.primaria },
        { title: 'Scuola media', levels: italianLevels.medie },
        { title: 'Liceo', levels: italianLevels.liceo }
      ];
    }

  // fallback / english or unknown
    return [
      { title: 'Elementary School', levels: genericLevels.elementary },
      { title: 'Middle School', levels: genericLevels.middle },
      { title: 'High School', levels: genericLevels.high }
    ];
  };

  const loaderMessage = t('onboardingLoading');
  const loaderText = loaderMessage && loaderMessage !== 'onboardingLoading'
    ? loaderMessage
    : 'Loading next step...';

  return (
    <div className={`onboarding-container ${isDarkTheme ? 'dark' : 'light'}`}>
      <div className="onboarding-stage">
        <div className="stage-header">
          <img src={isDarkTheme ? cap : capLight} alt="MathBox cap" className="stage-header-icon" />
          <h1 className="onboarding-title">{t('onboardingTitle')}</h1>
        </div>

        <div className="step-progress">
          {[0, 1, 2, 3, 4].map((step) => (
            <span
              key={step}
              className={`progress-dot ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'complete' : ''}`}
            />
          ))}
        </div>

        {isTransitioning ? (
          <div className="onboarding-loader">
            <div className="loader-spinner" />
            <p className="loader-text">{loaderText}</p>
          </div>
        ) : (
          <>
            {currentStep === 0 && (
              <div className="language-selection-section">
                <div className="language-content">
                  <h2 className="language-title">{t('languageTitle')}</h2>
                  <div className="language-flags">
                    {languages.map((lng) => (
                      <button
                        type="button"
                        key={lng.id}
                        className="language-flag-container"
                        onClick={() => handleLanguageSelect(lng.id, lng.code)}
                        aria-label={lng.name}
                      >
                        <img src={lng.flag} alt={lng.name} className="language-flag" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="education-selection-section">
                <div className="edu-content">
                  <h1 className="onboarding-title">Which country curriculum are you following?</h1>
                  <div className="edu-lvl-bottom">
                    <div className="edu-lvl-btn-container">
                      {countries.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className={`button ${country === item.id ? 'selected' : ''}`}
                          onClick={() => handleCountrySelect(item.id)}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="education-selection-section">
                <div className="edu-content">
                  <h1 className="onboarding-title">{t('educationTitle')}</h1>
                  <div className="edu-lvl-bottom">
                    <div className="edu-lvl-btn-container">
                      {getEduGroups().map((group) => (
                        <div className="edu-group" key={group.title}>
                          <h3>{group.title}</h3>
                          <div className="edu-options">
                            {group.levels.map((lvl) => (
                              <button
                                type="button"
                                key={lvl}
                                className={`button ${education === lvl ? 'selected' : ''}`}
                                onClick={() => handleEducationSelect(lvl)}
                              >
                                {lvl}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="education-selection-section">
                <div className="edu-content diagnostic-content">
                  <h1 className="onboarding-title">Adaptive Competence Test</h1>
                  {!diagnosticResult && diagnosticSession && (
                    <>
                      <p className="diagnostic-phase-label">
                        {diagnosticSession.phase === 'phase1' ? 'Phase 1: Initial Questions' : 'Phase 2: Adaptive Questions'}
                      </p>
                      {(() => {
                        const questions = diagnosticSession.phase === 'phase1'
                          ? diagnosticSession.phase1Questions
                          : diagnosticSession.phase2Questions;
                        const currentQuestion = questions[diagnosticSession.currentIndex];
                        return currentQuestion ? (
                          <div className="diagnostic-question-card">
                            <h3>{currentQuestion.prompt}</h3>
                            <div className="edu-lvl-btn-container diagnostic-options">
                              {currentQuestion.choices.map((choice, index) => (
                                <button
                                  type="button"
                                  key={choice}
                                  className="button"
                                  onClick={() => handleDiagnosticAnswer(index)}
                                >
                                  {choice}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </>
                  )}
                  {diagnosticResult && (
                    <div className="diagnostic-result-card">
                      <h3>Results</h3>
                      <p><strong>Estimated school level:</strong> {diagnosticResult.estimatedSchoolLevel}</p>
                      <p><strong>Classification:</strong> {diagnosticResult.classification}</p>
                      <p><strong>Confidence score:</strong> {diagnosticResult.confidenceScore}%</p>
                      <div>
                        <strong>Recommended starting units:</strong>
                        <ul>
                          {diagnosticResult.recommendedUnits.map((unit) => (
                            <li key={unit}>{unit}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        className="button"
                        onClick={() => advanceToStep(4)}
                      >
                        Continue
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="goal-selection-section">
                <div className="goal-content">
                  <h1 className="onboarding-title">{t('goalTitle')}</h1>
                  <div className="edu-lvl-btn-container">
                    <div className="top-part-goal">
                      <div className="left-goal-btns">
                        <button
                          type="button"
                          className={`button ${goal === 'Improve grades' ? 'selected' : ''}`}
                          onClick={() => handleGoalSelect('Improve grades')}
                        >
                          Improve grades
                        </button>
                        <button
                          type="button"
                          className={`button ${goal === 'Understand Better' ? 'selected' : ''}`}
                          onClick={() => handleGoalSelect('Understand Better')}
                        >
                          Understand Better
                        </button>
                      </div>
                      <div className="goal-image">
                        <img src={goalImg} alt="Goal illustration" />
                      </div>
                      <div className="right-goal-btns">
                        <button
                          type="button"
                          className={`button ${goal === 'Prepare for exams' ? 'selected' : ''}`}
                          onClick={() => handleGoalSelect('Prepare for exams')}
                        >
                          Prepare for exams
                        </button>
                        <button
                          type="button"
                          className={`button ${goal === 'Review past topics' ? 'selected' : ''}`}
                          onClick={() => handleGoalSelect('Review past topics')}
                        >
                          Review past topics
                        </button>
                      </div>
                    </div>
                    <div className="bottom-goal-btn">
                      <button
                        type="button"
                        className={`button ${goal === 'Curiosity / Fun' ? 'selected' : ''}`}
                        onClick={() => handleGoalSelect('Curiosity / Fun')}
                      >
                        Curiosity / Fun
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
